import { api } from '../axios'
import type { ApiResponse } from '../../../types'

export interface NonTeachingStaff {
  id: number
  full_name: string
  date_of_birth: string
  gender: 'Male' | 'Female' | 'Others'
  parent_name: string
  designation: string
  type_of_appointment: string
  appointment_type_id?: number | null
  office_name?: string | null
  office_address?: string | null
  mobile?: string | null
  email?: string | null
  blood_group?: string | null
  identification_mark?: string | null
  photo_url: string
  non_teacher_id?: string | null
  jurisdiction_type: 'DIRECTORATE' | 'SECRETARIAT'
  status: 'active' | 'inactive'
  inactive_reason?: string | null
  created_by: number
  created_at: string
  updated_at?: string | null
  schools?: {
    school_id: string
    school_name: string
  }
}

export interface CreateNonTeachingStaffRequest {
  full_name: string
  date_of_birth: string
  gender: 'Male' | 'Female' | 'Others'
  parent_name: string
  designation: string
  /** Master data `appointment_types.id`. Stored label is the type name (e.g. "Lumpsum Aided"). */
  appointment_type_id: number
  type_of_appointment?: string
  place_of_posting_name?: string
  office_name?: string | null
  office_address?: string | null
  mobile?: string | null
  email?: string | null
  blood_group?: string | null
  identification_mark?: string | null
  photo_url: string
  non_teacher_id?: string | null
}

export interface UpdateNonTeachingStaffRequest extends Partial<CreateNonTeachingStaffRequest> {
  status?: 'active' | 'inactive'
  inactive_reason?: string | null
}

export interface NonTeachingStaffListParams {
  page?: number
  limit?: number
  q?: string
  status?: 'active' | 'inactive'
  school_id?: number
}

export interface NonTeachingStaffListResponse {
  data: NonTeachingStaff[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export const nonTeachingStaffApi = {
  /**
   * List non-teaching staff
   */
  list: (params?: NonTeachingStaffListParams) =>
    api.get<ApiResponse<NonTeachingStaffListResponse>>('/non-teaching-staff', { params }),

  /**
   * Get non-teaching staff by ID
   */
  getById: (id: number) =>
    api.get<ApiResponse<NonTeachingStaff>>(`/non-teaching-staff/${id}`),

  /**
   * Create non-teaching staff
   */
  create: (data: CreateNonTeachingStaffRequest) =>
    api.post<ApiResponse<NonTeachingStaff>>('/non-teaching-staff', data),

  /**
   * Update non-teaching staff
   */
  update: (id: number, data: UpdateNonTeachingStaffRequest) =>
    api.put<ApiResponse<NonTeachingStaff>>(`/non-teaching-staff/${id}`, data),

  /**
   * Soft delete non-teaching staff
   */
  softDelete: (id: number) =>
    api.delete<ApiResponse<void>>(`/non-teaching-staff/${id}`),

  /**
   * Request permanent delete (hard delete) - requires reason
   */
  requestPermanentDelete: (id: number, reason: string) =>
    api.post<ApiResponse<{ requestId?: number }>>(`/non-teaching-staff/${id}/hard-delete`, { reason })
}
